import { cloneDeep, isNil } from "lodash-es";
import type { FieldConfig, VariableConfig } from "../fields/types";
import type { FieldConfigDto, SubformDto } from "../services/ApiService";
import type { Favourite } from "../services/FavouriteService";
import {
  type FormScope,
  type ReferenceValueAction,
  type Rule,
  RuleActionKey,
  type Subform,
} from "../state/formVersion";
import { toRule } from "./mappers/rule";
import SubformId from "../state/SubformId";
import { toField, widgetUidToField } from "../fields/fields";
import FieldId from "../fields/FieldId";
import type { CalculationFieldProperties } from "../fields/number/CalculationField";
import VariableValueReference from "../variables/VariableValueReference";
import FieldValueReference from "../fields/FieldValueReference";
import { toSubformEmailConfiguration } from "./mappers/subformEmailConfiguration";
import { createReference, toVariableMap } from "./valueReferenceUtil";
import type { TimeDifferenceFieldProperties } from "../fields/dateTime/TimeDifferenceField";
import type { SubformFieldProperties } from "../fields/subform/SubformField";
import type { StringTemplate, StringTemplateTypes } from "./stringTemplateUtil";
import MetaVariableReference from "../metaVariables/MetaVariableReference";
import type { MetaVariableId } from "../metaVariables/metaVariable";
import logger from "./logger";
import FormServicesFactory from "../services/FormServicesFactory";
import type { PinFieldProperties } from "../fields/media/PinField";
import type { PhotoFieldProperties } from "../fields/media/PhotoField";
import type { DrawingFieldProperties } from "../fields/media/DrawingField";
import type { FileFieldProperties } from "../fields/media/FileField";

type PinDto = {
  form?: SubformDto;
  name?: string;
  target_form_id?: SubformId;
};

// A favourite stores references as plain id strings, so a field and a variable are indistinguishable by shape.
// Resolve them the way `toFormVersion` does: against the variables of the form version we are restoring into.
const mapValueReference = (
  reference: FieldValueReference | VariableValueReference | string,
  fieldMap: Map<string, string>,
  variables: VariableConfig[],
): FieldValueReference | VariableValueReference => {
  if (reference instanceof VariableValueReference) {
    // Variables are not part of the favourite, so they are never remapped.
    return reference;
  }

  const value = reference.toString();
  // A reference can carry a path (e.g. `<searchFieldId>.<column>`), but only the id is remapped.
  const [id, ...path] = value.split(".");
  const mappedId = fieldMap.get(id);

  if (isNil(mappedId)) {
    // Not part of the favourite, so it is either a variable or a field of the form we are restoring into.
    return createReference(value, variables);
  }
  return FieldValueReference.from([mappedId, ...path].join("."));
};

// Used for references that are already known to point at a field, where only the id has to be remapped.
const remapFieldReference = (
  fieldValueReference: FieldValueReference,
  fieldMap: Map<string, string>,
): FieldValueReference => {
  const [id, ...path] = fieldValueReference.toString().split(".");
  const mappedId = fieldMap.get(id);

  if (isNil(mappedId)) {
    // This is a reference to a normal field (not inside a subform), and does not need to be remapped.
    // `fromDto` in `toFavourite` fails to type them correctly, though, so we need to ensure the type here.
    return fieldValueReference instanceof FieldValueReference
      ? fieldValueReference
      : FieldValueReference.from(fieldValueReference);
  }

  // Keep the reference type, so an image placeholder does not degrade into a text one.
  const type = fieldValueReference instanceof FieldValueReference ? fieldValueReference.type : undefined;
  return new FieldValueReference(FieldId.from(mappedId), path, type);
};

const mapFieldId = (fieldId: FieldId, fieldMap: Map<string, string>): FieldId => {
  const mappedFieldId = fieldMap.get(fieldId.toString());

  if (isNil(mappedFieldId)) {
    logger.error("Failed to map field id inside a favourite");
    return fieldId;
  }
  return FieldId.from(mappedFieldId);
};

// `fromDto` in `toFavourite` cannot revive the reference classes of a stored string template, so its values can still
// be raw id strings. Anything reading them back (the placeholder editor, for one) expects a reference instance.
const toReferenceEntry = (entry: StringTemplateTypes): StringTemplateTypes => {
  switch (entry.type) {
    case "field":
      return entry.value instanceof FieldValueReference
        ? entry
        : { ...entry, value: FieldValueReference.from(String(entry.value)) };
    case "variable":
      return entry.value instanceof VariableValueReference
        ? entry
        : { ...entry, value: VariableValueReference.from(String(entry.value)) };
    case "metaVariable":
      return entry.value instanceof MetaVariableReference
        ? entry
        : { ...entry, value: MetaVariableReference.from(String(entry.value) as MetaVariableId) };
    default:
      return entry;
  }
};

const mapIdsOfStringTemplate = (stringTemplate: StringTemplate, fieldMap: Map<string, string>): StringTemplate =>
  stringTemplate.map((entry) => {
    const value = toReferenceEntry(entry);
    if (value.type === "field") {
      return { ...value, value: remapFieldReference(value.value, fieldMap) };
    }
    return value;
  });

const toFieldMap = (fields: FieldConfig[]) =>
  fields.reduce((acc, field) => {
    acc.set(field.uid.toString(), field);
    return acc;
  }, new Map<string, FieldConfig>());

const mapField = (
  fieldDto: FieldConfigDto,
  formScope: FormScope,
  domainObjects: DomainObjectsWithIdMap,
  variables: VariableConfig[],
): void => {
  const field = widgetUidToField(fieldDto.widget);
  const additionalProps = field.fromDto(fieldDto.properties) as Record<string, unknown>;

  if (
    fieldDto.widget === "com.moreapps:detail:1" &&
    !isNil((additionalProps as unknown as { form: SubformDto; target_form_id?: SubformId })?.form)
  ) {
    const subformDto = additionalProps.form as SubformDto;
    const subformId = mapSubformDto(
      subformDto,
      domainObjects,
      subformDto.meta?.name ?? (additionalProps?.label_text as string) ?? "",
      variables,
    );
    additionalProps.target_form_id = subformId;
  } else if (
    fieldDto.widget === "com.moreapps:pin:1" &&
    !isNil((additionalProps as unknown as { pins: PinDto[] })?.pins)
  ) {
    (additionalProps.pins as PinDto[]).forEach((pin) => {
      if ("form" in pin) {
        const subformDto = pin.form as SubformDto;
        const subformId = mapSubformDto(
          subformDto,
          domainObjects,
          subformDto.meta?.name ?? (pin?.name as string) ?? "",
          variables,
        );

        delete pin.form;

        pin.target_form_id = subformId;
      }
    });
  }
  if ("form" in additionalProps) {
    delete additionalProps.form;
  }

  const fieldConfig = field.init(formScope, additionalProps);
  domainObjects.fields = [...domainObjects.fields, fieldConfig];
  domainObjects.idMap.set(fieldDto.uid, fieldConfig.uid.toString());
};

const mapSubformDto = (
  subformDto: SubformDto,
  domainObjects: DomainObjectsWithIdMap,
  label: string,
  variables: VariableConfig[],
): SubformId => {
  // Return existing subform when subform already exists in favourite context.
  const existingSubformId = domainObjects.idMap.get(subformDto.uid);
  if (!isNil(existingSubformId)) {
    return SubformId.from(existingSubformId);
  }

  const subform = {
    uid: SubformId.random(),
    meta: {
      name: label ?? "",
    },
  };

  if (!isNil(subformDto.fields)) {
    subformDto.fields
      .filter((field) => !isNil(field))
      .forEach((field) => {
        mapField(field, subform.uid, domainObjects, variables);
      });
  }

  domainObjects.subforms = [
    ...domainObjects.subforms,
    {
      ...subform,
      emailConfiguration: toSubformEmailConfiguration(
        subformDto,
        domainObjects.fields.filter((field) => subform.uid.equals(field.scope.toString())),
        variables,
      ),
    },
  ];
  domainObjects.idMap.set(subformDto.uid, subform.uid.toString());

  if (!isNil(subformDto.rules) && subformDto.rules?.length > 0) {
    const fieldMap = toFieldMap(domainObjects.fields);
    const rules: Rule[] = subformDto.rules.map((ruleDto) =>
      toRule(ruleDto, fieldMap, toVariableMap(variables), subform.uid),
    );
    domainObjects.rules = [...domainObjects.rules, ...rules];
  }

  return subform.uid;
};

export type DomainObjects = {
  fields: FieldConfig<Record<string, unknown>>[];
  subforms: Subform[];
  rules: Rule[];
};

type DomainObjectsWithIdMap = DomainObjects & {
  idMap: Map<string, string>;
};

export const mapFavouriteToDomainObjects = (favourite: Favourite, formScope: FormScope): DomainObjects => {
  const domainObjects: DomainObjectsWithIdMap = {
    fields: [],
    subforms: [],
    rules: [],
    idMap: new Map<string, string>(),
  };

  const { fieldService, variableService } = FormServicesFactory.forScope(formScope);
  const variables = variableService.getVariables();

  const additionalProps = cloneDeep(favourite.changedProps) as Record<string, unknown>;

  if (favourite.field.type === "subform" && !isNil(additionalProps.form)) {
    const subformDto = additionalProps.form as SubformDto;
    const subformId = mapSubformDto(
      subformDto,
      domainObjects,
      subformDto.meta?.name ?? (additionalProps?.label_text as string) ?? "",
      variables,
    );

    delete additionalProps.form;
    additionalProps.target_form_id = subformId;
  } else if (favourite.field.type === "pin" && !isNil((additionalProps as { pins: unknown[] })?.pins)) {
    (additionalProps.pins as PinDto[]).forEach((pin) => {
      if ("form" in pin) {
        const subformDto = pin.form as SubformDto;
        const subformId = mapSubformDto(
          subformDto,
          domainObjects,
          subformDto.meta?.name ?? (pin?.name as string) ?? "",
          variables,
        );

        delete pin.form;
        pin.target_form_id = subformId;
      }
    });
  }

  const fieldConfig = fieldService.createField(favourite.field.type, additionalProps);
  domainObjects.fields = [...domainObjects.fields, fieldConfig];
  domainObjects.idMap.set(favourite.id, fieldConfig.uid.toString());

  domainObjects.rules.forEach((rule) => {
    rule.conditions.forEach((condition) => {
      if (condition.type === "FIELD" || condition.type === "FIELD_DATA_SOURCE") {
        condition.fieldId = mapFieldId(condition.fieldId, domainObjects.idMap);
      }
    });

    rule.actions.forEach((action) => {
      if (!isNil(action.fieldId)) {
        action.fieldId = mapFieldId(action.fieldId, domainObjects.idMap);
      }

      if (!isNil((action as ReferenceValueAction).value) && action?.key === RuleActionKey.REFERENCE) {
        if (!isNil(action.value)) {
          action.value = mapFieldId(action.value as FieldId, domainObjects.idMap);
        }
      }
    });
  });

  domainObjects.fields.forEach((fieldConfig) => {
    const field = toField(fieldConfig.type);

    fieldConfig.properties = field.postProcessFromDto(
      fieldConfig.properties,
      domainObjects.fields,
      variables,
      fieldConfig.scope,
    ) as Record<string, unknown>;

    if (fieldConfig.type === "subform" && !isNil((fieldConfig.properties as SubformFieldProperties).itemHtml)) {
      (fieldConfig.properties as SubformFieldProperties).itemHtml = mapIdsOfStringTemplate(
        (fieldConfig.properties as SubformFieldProperties).itemHtml,
        domainObjects.idMap,
      );
    } else if (fieldConfig.type === "photo" || fieldConfig.type === "drawing" || fieldConfig.type === "file") {
      const mediaField = fieldConfig as FieldConfig<
        PhotoFieldProperties | DrawingFieldProperties | FileFieldProperties
      >;

      if (!isNil(mediaField.properties.filename)) {
        mediaField.properties.filename = mapIdsOfStringTemplate(mediaField.properties.filename, domainObjects.idMap);
      }
    } else if (fieldConfig.type === "pin") {
      const pinField = fieldConfig as FieldConfig<PinFieldProperties>;

      if (!isNil(pinField.properties.filename)) {
        pinField.properties.filename = mapIdsOfStringTemplate(pinField.properties.filename, domainObjects.idMap);
      }

      if (!isNil(pinField.properties.pins)) {
        pinField.properties.pins = pinField.properties.pins.map((pin) => {
          if (!isNil(pin.itemMarkup)) {
            pin.itemMarkup = mapIdsOfStringTemplate(pin.itemMarkup, domainObjects.idMap);
          }
          return pin;
        });
      }
    } else if (fieldConfig.type === "calculation" && !isNil(fieldConfig.properties.terms)) {
      (fieldConfig as FieldConfig<CalculationFieldProperties>).properties.terms = (
        fieldConfig as FieldConfig<CalculationFieldProperties>
      ).properties.terms.map((term) => mapValueReference(term, domainObjects.idMap, variables));
    } else if (fieldConfig.type === "timeDifference") {
      const timeDifference = fieldConfig as FieldConfig<TimeDifferenceFieldProperties>;

      if (!isNil(timeDifference.properties.start)) {
        timeDifference.properties.start = mapValueReference(
          timeDifference.properties.start,
          domainObjects.idMap,
          variables,
        );
      }

      if (!isNil(timeDifference.properties.end)) {
        timeDifference.properties.end = mapValueReference(
          timeDifference.properties.end,
          domainObjects.idMap,
          variables,
        );
      }
    }
  });

  delete (domainObjects as Partial<DomainObjectsWithIdMap>).idMap;
  return domainObjects;
};
